import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User,
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, db, storage } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';

// Definér typen for AuthContext
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  logOut: () => Promise<void>;
  userType: 'agent' | 'management' | null;
  setUserType: (type: 'agent' | 'management' | null) => void;
  userName: string | null;
  profileImageUrl: string | null;
  setProfileImageUrl: (url: string | null) => void;
  refreshProfileImage: () => Promise<void>;
}

// Opret AuthContext
const AuthContext = createContext<AuthContextType | null>(null);

// Hook til at bruge AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth skal bruges inden for en AuthProvider');
  }
  return context;
};

// Props type for AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider-komponent
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<'agent' | 'management' | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  // Funktion til at regenerere et frisk profilbillede-URL
  const refreshProfileImage = async () => {
    if (!currentUser) return;
    
    try {
      console.log("Forsøger at opdatere profilbillede URL...");
      
      // Tjek om brugeren har et profil-dokument
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().profileImageUrl) {
        // Opbyg en frisk reference til billedet i storage
        try {
          const storageRef = ref(storage, `profile_images/${currentUser.uid}`);
          const freshURL = await getDownloadURL(storageRef);
          console.log("Nyt profilbillede URL genereret:", freshURL.substring(0, 30) + "...");
          
          // Opdater state med det nye URL
          setProfileImageUrl(freshURL);
          
          // Opdater også Firestore med det friske URL
          await setDoc(userRef, { 
            ...userDoc.data(), 
            profileImageUrl: freshURL 
          }, { merge: true });
          
          console.log("Firestore opdateret med frisk URL");
        } catch (storageError) {
          console.error("Fejl ved hentning af nyt profilbillede:", storageError);
          // Behold den gamle URL hvis vi ikke kan opdatere
        }
      }
    } catch (error) {
      console.error("Fejl ved opdatering af profilbillede URL:", error);
    }
  };

  // Signup-funktion
  const signUp = (email: string, password: string) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  // Login-funktion
  const signIn = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Logout-funktion
  const logOut = () => {
    return signOut(auth);
  };

  // Lyt til auth-tilstandsændringer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth tilstand ændret:", user ? "Bruger logget ind" : "Ingen bruger");
      setCurrentUser(user);
      
      // Hvis brugeren er logget ind, hent brugertypen fra Firestore
      if (user) {
        try {
          console.log("Forsøger at hente brugerdata for:", user.uid);
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            console.log("Brugerdata hentet succesfuldt:", userData);
            setUserType(userData.role);
            
            // Gem brugerens navn hvis det findes
            if (userData.name) {
              setUserName(userData.name);
              console.log("Brugernavn indlæst:", userData.name);
            } else {
              console.log("Intet brugernavn fundet i databasen");
              setUserName(null);
            }
            
            // Gem brugerens profilbillede URL hvis det findes
            if (userData.profileImageUrl) {
              console.log("Profilbillede URL fundet i database, forsøger at opdatere...");
              // Først indlæs det URL vi har i databasen
              setProfileImageUrl(userData.profileImageUrl);
              
              // Derefter prøv at få et frisk URL (asynkront)
              refreshProfileImage()
                .catch(err => console.error("Kunne ikke opdatere profilbillede efter login:", err));
            } else {
              console.log("Intet profilbillede URL fundet i databasen");
              setProfileImageUrl(null);
            }
          } else {
            // Brugeren findes ikke i Firestore - ingen automatisk oprettelse
            console.warn("Ingen brugerdata fundet i Firestore for:", user.uid);
            setUserType(null);
            setUserName(null);
            setProfileImageUrl(null);
          }
        } catch (error) {
          console.error('Fejl ved hentning af brugerdata:', error);
          setUserType(null);
          setUserName(null);
          setProfileImageUrl(null);
        }
      } else {
        // Nulstil brugertype og navn når brugeren logger ud
        setUserType(null);
        setUserName(null);
        setProfileImageUrl(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Værdien, der skal leveres til konteksten
  const value = {
    currentUser,
    loading,
    signUp,
    signIn,
    logOut,
    userType,
    setUserType,
    userName,
    profileImageUrl,
    setProfileImageUrl,
    refreshProfileImage
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 